import React, { useState, useEffect } from 'react';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import { get, patch } from '../../axios/apiService';
import MainCard from 'ui-component/cards/MainCard';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import { Grid, Stack, FormControl, OutlinedInput, InputAdornment, Modal, Box, Typography, IconButton, Button, TextField, Select, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { Chip } from '@mui/material';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

const Inquiry = () => {
    const [isLoading, setLoading] = useState(false);
    const [inquiries, setInquiries] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [modalMode, setModalMode] = useState(''); // 'view' or 'edit'
    const [editRemark, setEditRemark] = useState('');
    const [editStatus, setEditStatus] = useState('');
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const { hasWritePermission } = usePermissions();
    const canEdit = hasWritePermission(MODULE_KEYS.Inquiry)

    // Status mapping
    const statusMapReverse = {
        0: 'Open',
        1: 'InProgress',
        2: 'Resolved'
    };

    const mapStatusFromBackend = (status) => {
        if (typeof status === 'number') return statusMapReverse[status] || 'Open';
        if (typeof status === 'string') return status;
        return 'Open';
    };


    const statusMap = {
        Open: 0,
        InProgress: 1,
        Resolved: 2
    };

    const fetchInquiries = async () => {
        setLoading(true);
        try {
            const sortField = sortModel.length > 0 ? sortInquiryField(sortModel[0].field) : 'CreatedDate';
            const sortOrder = sortModel.length > 0 ? (sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending') : 'Descending';
            const sortQuery = `&$orderby=${sortField}&$orderbydirection=${sortOrder}`;
            const searchQuery = search !== '' ? `&$filter=Name~LIKE~${search} OR EmailAddress~LIKE~${search}` : '';

            let response = await get(
                `ContactUs?$skip=${paginationModel.page * paginationModel.pageSize}&$top=${paginationModel.pageSize}${searchQuery}${sortQuery}`
            );

            if (response && Array.isArray(response.values)) {
                const transformedInquiries = response.values.map((inquiry) => ({
                    id: inquiry.id || '',
                    name: inquiry.name || '',
                    email: inquiry.emailAddress || '',
                    subject: inquiry.subject || '',
                    message: inquiry.message || '',
                    status: mapStatusFromBackend(inquiry.status),
                    remark: inquiry.remark || '',
                    createdAt: inquiry.createdDate ? dayjs(inquiry.createdDate).format('DD-MMM-YYYY') : ''
                }));

                setInquiries(transformedInquiries);
                setRowCount(response.totalCount || 0);
            } else {
                setInquiries([]);
                setRowCount(0);
            }
        } catch (err) {
            console.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInquiries();
    }, [paginationModel, sortModel, search]);

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleSortModelChange = (model) => {
        setSortModel(model);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const sortInquiryField = (field) => {
        switch (field) {
            case 'name':
                return 'Name';
            case 'email':
                return 'EmailAddress';
            case 'subject':
                return 'Subject';
            case 'status':
                return 'Status';
            case 'createdAt':
                return 'CreatedDate';
            default:
                return 'CreatedDate';
        }
    };

    const handleView = (row) => {
        setSelectedInquiry(row);
        setModalMode('view');
        setOpen(true);
    };

    const handleEdit = (row) => {
        setSelectedInquiry(row);
        setEditRemark(row.remark || '');
        setEditStatus(row.status || 'Open');
        setModalMode('edit');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setModalMode('');
        setEditRemark('');
        setEditStatus('');
        setSelectedInquiry(null);
    };

    const handleSave = async () => {
        if (!selectedInquiry?.id) return handleClose();
        setLoading(true);
        try {
            const payload = {
                remark: editRemark,
                status: statusMap[editStatus]
            };
            await patch(`ContactUs/${selectedInquiry.id}`, payload);
            toast.success('Inquiry updated successfully');
            await fetchInquiries();
            handleClose();
        } catch (err) {
            console.error('Update failed:', err);
            toast.error('Failed to update inquiry');
        } finally {
            setLoading(false);
        }
    };

    // Action buttons
    const ActionButtons = (row) => (
        <Stack direction="row" spacing={1}>
            <IconButton color="primary" onClick={() => handleView(row)}>
                <VisibilityIcon />
            </IconButton>
            {
                canEdit && (
                    <IconButton color="primary" onClick={() => handleEdit(row)}>
                        <EditIcon />
                    </IconButton>
                )
            }
        </Stack>
    );

    // Grid columns for inquiry table
    const columns = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1 },
        { field: 'subject', headerName: 'Subject', flex: 1 },
        { field: 'createdAt', headerName: 'Created Date', flex: 1 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) =>
                params.row.status === 'Resolved' ? (
                    <Chip label="Resolved" color="success" size="small" />
                ) : params.row.status === 'InProgress' ? (
                    <Chip label="In Progress" color="warning" size="small" />
                ) : (
                    <Chip label="Open" color="error" size="small" />
                )
        },
        {
            field: 'action',
            headerName: 'Actions',
            renderCell: (params) => <ActionButtons {...params.row} />,
            align: 'right',
            width: 180,
            sortable: false,
            filterable: false,
            flex: 1
        }
    ];

    return (
        <>
            <MainCard title="Inquiry Management">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0}>
                            <form noValidate autoComplete="off" className="formSearch">
                                <FormControl sx={{ width: '25ch' }} size="small">
                                    <OutlinedInput
                                        placeholder="Search"
                                        value={search}
                                        onChange={handleSearchChange}
                                        endAdornment={
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </form>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <DataGrid
                            getRowId={(row) => row.id}
                            rows={inquiries}
                            rowCount={rowCount}
                            columns={columns}
                            paginationMode="server"
                            sortingMode="server"
                            pageSizeOptions={[10]}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            onProcessRowUpdateError={(error) => console.error('Row update error:', error)}
                            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            autoHeight={true}
                        />
                    </Grid>
                </Grid>
            </MainCard>

            {/* View/Edit Modal */}
            <Modal open={open} onClose={handleClose}>
                <Box className="modalBox">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>
                            {modalMode === 'view' ? 'Inquiry Details' : 'Edit Inquiry'}
                        </Typography>
                        <IconButton onClick={handleClose} sx={{ color: 'red' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    {modalMode === 'view' ? (
                        <>
                            <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                Message:
                            </Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                                {selectedInquiry?.message || 'No message'}
                            </Typography>
                            <Typography variant="subtitle1">
                                Remark:
                            </Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                                {selectedInquiry?.remark || 'No remark'}
                            </Typography>
                            <Typography variant="subtitle1">
                                Status:
                            </Typography>
                            {selectedInquiry?.status === 'Resolved' ? (
                                <Chip label="Resolved" color="success" size="small" />
                            ) : selectedInquiry?.status === 'InProgress' ? (
                                <Chip label="In Progress" color="warning" size="small" />
                            ) : (
                                <Chip label="Open" color="error" size="small" />
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                                <Button onClick={handleClose} variant="contained" color="primary" sx={{ mt: 2 }}>
                                    Close
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={editRemark}
                                onChange={(e) => setEditRemark(e.target.value)}
                                label="Remark"
                                variant="outlined"
                                sx={{ mt: 2 }}
                            />
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                value={editStatus}
                                onChange={(e) => setEditStatus(e.target.value)}
                                variant="outlined"
                                sx={{ mt: 2 }}
                            >
                                <MenuItem value="Open">Open</MenuItem>
                                <MenuItem value="InProgress">In Progress</MenuItem>
                                <MenuItem value="Resolved">Resolved</MenuItem>
                            </TextField>
                            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                                <Button onClick={handleClose} variant="contained" color="error" sx={{ mt: 2, mr: 2 }}>
                                    Cancel
                                </Button>
                                <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleSave}>
                                    Save
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            <LoadingBackdrop open={isLoading} />
        </>
    );
};

export default Inquiry;